import { Injectable } from '@angular/core';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics, isSupported, logEvent } from 'firebase/analytics';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private app: FirebaseApp;
  private analytics: Analytics | null = null;

  constructor() {
    // Initialize Firebase
    this.app = initializeApp(environment.firebase);
    
    // Initialize Analytics - try to initialize regardless of environment
    // This allows tracking visits from browsers, bots, crawlers, etc.
    this.initializeAnalytics();
  }

  private initializeAnalytics(): void {
    // Try to initialize Analytics - works in browser and browser-like environments
    // Will fail gracefully if not supported (non-browser environments)
    if (typeof window !== 'undefined') {
      isSupported().then((supported) => {
        if (supported) {
          try {
            this.analytics = getAnalytics(this.app);
          } catch (error) {
            // Silently fail - Analytics not available in this environment
          }
        }
      }).catch(() => {
        // Silently fail - Analytics not supported
      });
    } else {
      // Try to initialize anyway (for non-standard environments)
      // This helps catch visits from automated tools that might load the app
      try {
        this.analytics = getAnalytics(this.app);
      } catch (error) {
        // Silently fail - Analytics requires browser environment
      }
    }
  }

  getAnalytics(): Analytics | null {
    return this.analytics;
  }

  getApp(): FirebaseApp {
    return this.app;
  }

  // Helper method to log events
  logEvent(eventName: string, eventParams?: { [key: string]: any }): void {
    if (this.analytics) {
      try {
        logEvent(this.analytics, eventName, eventParams);
      } catch (error) {
        // Silently fail - don't break the app if Analytics fails
      }
    }
  }

  // Check if Analytics is ready
  isAnalyticsReady(): boolean {
    return this.analytics !== null;
  }
}
